import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, RotateCcw } from "lucide-react";

const questions = [
  {
    id: "energie",
    prompt: "Hoe ervaar je je energie op dit moment?",
    options: [
      { label: "Ik voel me meestal moe of leeg", weight: 3 },
      { label: "Wisselend, sommige dagen wel, andere niet", weight: 2 },
      { label: "Redelijk, maar ik zou meer willen kunnen", weight: 1 },
      { label: "Best goed", weight: 0 },
    ],
  },
  {
    id: "structuur",
    prompt: "Heb je een vaste dagstructuur?",
    options: [
      { label: "Nee, mijn dagen lopen door elkaar", weight: 3 },
      { label: "Een beetje, maar niet stabiel", weight: 2 },
      { label: "Wel, maar ik zou meer richting willen", weight: 1 },
      { label: "Ja, ik heb een goed ritme", weight: 0 },
    ],
  },
  {
    id: "verleden",
    prompt: "Heb je eerder een traject of hulp geprobeerd?",
    options: [
      { label: "Ja, meerdere keren, zonder blijvend effect", weight: 3 },
      { label: "Ja, één keer, het klikte niet", weight: 2 },
      { label: "Ooit iets, maar lang geleden", weight: 1 },
      { label: "Nee, dit is voor mij nieuw", weight: 1 },
    ],
  },
  {
    id: "toekomst",
    prompt: "Wat zou je willen veranderen?",
    options: [
      { label: "Ik weet het eigenlijk niet meer", weight: 3 },
      { label: "Rust in mijn hoofd en lijf", weight: 2 },
      { label: "Meer verbinding met mensen", weight: 2 },
      { label: "Weer iets betekenisvols doen, vrijwilligerswerk, opleiding of werk", weight: 1 },
    ],
  },
];

function outcome(score) {
  if (score <= 3) {
    return {
      title: "Je staat al aardig op weg.",
      body:
        "Klinkt alsof je richting hebt, LevelLeven zou je vooral kunnen versterken in de stappen naar duurzame participatie of werk. Neem gerust contact op om te kijken wat past.",
      accent: "#4A5D4E",
    };
  }
  if (score <= 7) {
    return {
      title: "Er is iets dat vastzit.",
      body:
        "Je herkent stukken van wat wij zien. LevelLeven werkt vóór re-integratie, eerst rust en fundament, dan pas de volgende stap. Dat lijkt precies waar jij nu zit.",
      accent: "#E06B3F",
    };
  }
  return {
    title: "Je bent moe. Dat is geen fout.",
    body:
      "LevelLeven is voor mensen die al langer stilstaan, niet omdat je niet wilt, maar omdat het fundament ontbreekt. Wij bouwen dat samen met jou op. In een vaste groep, met twee trainers, in jouw tempo.",
    accent: "#E06B3F",
  };
}

export default function Zelfscan() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const total = questions.length;
  const currentQ = questions[step];
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const res = done ? outcome(score) : null;

  const choose = (weight) => {
    const next = { ...answers, [currentQ.id]: weight };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setDone(false);
  };

  return (
    <div className="bg-[#FAF7F1] rounded-3xl border border-[#E5E1D8] overflow-hidden">
      <div className="p-8 sm:p-12">
        {!done ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F]">
                <Sparkles size={12} className="inline mr-2 -mt-0.5" />
                Zelfscan · vraag {step + 1} van {total}
              </p>
              <div className="flex gap-1.5" aria-hidden>
                {questions.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 w-6 rounded-full transition-colors ${
                      i <= step ? "bg-[#E06B3F]" : "bg-[#E5E1D8]"
                    }`}
                  />
                ))}
              </div>
            </div>

            <p
              data-testid="zelfscan-question"
              className="font-heading text-3xl sm:text-4xl text-[#1D2A22] leading-tight font-light"
            >
              {currentQ.prompt}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {currentQ.options.map((o, i) => (
                <button
                  key={i}
                  data-testid={`zelfscan-option-${step}-${i}`}
                  onClick={() => choose(o.weight)}
                  className="text-left rounded-2xl border border-[#E5E1D8] bg-[#FAF7F1] hover:bg-[#1D2A22] hover:text-[#FAF7F1] hover:border-[#1D2A22] p-5 transition-all duration-300 group"
                >
                  <span className="text-base leading-snug">{o.label}</span>
                  <ArrowUpRight
                    size={16}
                    className="inline ml-2 opacity-40 group-hover:opacity-100 transition-opacity"
                  />
                </button>
              ))}
            </div>

            <p className="mt-8 text-xs text-[#7A8276]">
              Geen goede of foute antwoorden. Deze scan is anoniem, er wordt niets opgeslagen.
            </p>
          </>
        ) : (
          <div data-testid="zelfscan-outcome">
            <p className="text-xs uppercase tracking-[0.25em] mb-5" style={{ color: res.accent }}>
              Wat wij horen
            </p>
            <p className="font-heading text-4xl sm:text-5xl text-[#1D2A22] leading-tight font-light">
              {res.title}
            </p>
            <p className="mt-6 text-lg text-[#4A5D4E] leading-relaxed font-light max-w-2xl">
              {res.body}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="zelfscan-cta-contact"
                className="group inline-flex items-center gap-3 bg-[#E06B3F] hover:bg-[#1D2A22] text-white rounded-full px-7 py-3.5 transition-colors duration-300"
              >
                <span className="text-base font-medium">Neem contact op</span>
                <ArrowUpRight size={16} />
              </Link>
              <button
                data-testid="zelfscan-restart"
                onClick={restart}
                className="inline-flex items-center gap-2 text-[#7A8276] hover:text-[#1D2A22] transition-colors"
              >
                <RotateCcw size={14} />
                Opnieuw beginnen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
